import { useEffect, useState } from 'react'
import { openWalkStream, WalkStreamEvent } from '../services/api/walk'

// Host-side presence listener for an active Walk With Me session.
//
// Opens a read-only subscription to the public walk SSE stream and surfaces
// the first real guest name it sees. The host's PresencePill consumes this
// to replace the "Someone you trust is walking with you" fallback with the
// recipient's actual name the moment they join.
//
// Known limitation: the public stream endpoint (/v1/walk/:token/stream) does
// not distinguish host from recipient connections — both use the same
// unauthenticated path. The host's own subscription will therefore fire a
// self-join presence echo. We suppress anything that arrives in the first
// 600ms after connect and ignore the "a friend" default sentinel that the
// backend assigns when the ?name= query param is absent.
//
// The cleaner long-term split is either an auth-aware filter on the backend
// (the host's authenticated subscription is excluded from presence broadcasts)
// or a dedicated /v1/walk/:token/host-presence channel. Both are backend
// changes. Tracking as v0.35 polish.
const SELF_ECHO_WINDOW_MS = 600
const DEFAULT_GUEST_SENTINEL = 'a friend'

export function useWalkPresence(token: string | null): string | null {
  const [guestName, setGuestName] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setGuestName(null)
      return
    }

    const openedAt = Date.now()
    const source = openWalkStream(token, (event: WalkStreamEvent) => {
      if (event.type !== 'presence') return

      if (Date.now() - openedAt < SELF_ECHO_WINDOW_MS) return

      if (event.status === 'left') {
        setGuestName(null)
        return
      }

      if (event.status === 'joined' && event.name) {
        if (event.name === DEFAULT_GUEST_SENTINEL) return
        setGuestName(event.name)
      }
    })

    return () => {
      source.close()
      setGuestName(null)
    }
  }, [token])

  return guestName
}
